import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact } from 'lodash-es';
import { getStatusBadgeSkin } from '~/business-logic/invoiceLogic';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { getAccountingStatusFilterOptions, getInvoiceStatusFilterOptions, getIssuesPreventingPaymentFilterOptions, getProcessedByFilterOptions, mergePaymentDetailsFilter } from '~/components/InvoicesTable/InvoicesTable.utils';
import { FilterType as InvoiceFilterType, getActiveFilterGroups, getTableFilterConfig } from '~/components/InvoicesTable/invoicesTableFilters';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceSummaryModel } from '~/models';
import { Accounting } from '~/types/accounting';
import { applyClientInvoicesFilters, applyClientInvoicesSort, ClientInvoicesHeaderId, EMPTY_FILTERS, getAdditionalInfoText, getClientInvoicesFilterCounters, getStatusText, parseDateRangeFilter } from './ClientInvoicesListView.utils';
export const invoiceColumnNames = (showAgreementName)=>[
        'invoiceNumber',
        ...showAgreementName ? [
            'agreementName'
        ] : [],
        'status',
        'additionalInfo',
        'issueDate',
        'dueDate',
        'total'
    ];
export const invoiceSortIds = (showAgreementName)=>[
        ClientInvoicesHeaderId.InvoiceId,
        ...showAgreementName ? [
            ClientInvoicesHeaderId.AgreementName
        ] : [],
        ClientInvoicesHeaderId.Status,
        ClientInvoicesHeaderId.AdditionalInfo,
        ClientInvoicesHeaderId.IssueDate,
        ClientInvoicesHeaderId.DueDate,
        ClientInvoicesHeaderId.Total
    ];
const DEFAULT_SORT_COLUMN_INDEX = 4;
export const defaultInvoicesSort = (showAgreementName)=>({
        name: invoiceColumnNames(showAgreementName)[DEFAULT_SORT_COLUMN_INDEX],
        direction: 'desc'
    });
const ACCOUNTING_FULL_NAME = {
    qbo: Accounting.QBO,
    xero: Accounting.Xero
};
const integrationStatus = (integration)=>{
    if (integration.isSynced()) return 'success';
    if (integration.isSyncFailed()) return 'error';
    if (integration.isPendingSync()) return 'pending';
    return undefined;
};
const integrationEntry = (integration, provider)=>{
    if (integration == null) return [];
    const status = integrationStatus(integration);
    return status == null ? [] : [
        {
            provider,
            status
        }
    ];
};
export const invoiceIntegrations = (param)=>{
    let { invoice, showQbo, showXero } = param;
    return invoice == null ? [] : [
        ...showQbo ? integrationEntry(invoice.integration, 'qbo') : [],
        ...showXero ? integrationEntry(invoice.xeroIntegration.getIntegration(), 'xero') : []
    ];
};
export const invoiceIntegrationTooltip = (entry)=>{
    const name = getAccountingDetails(ACCOUNTING_FULL_NAME[entry.provider]).fullName;
    if (entry.status === 'success') return "Invoice recorded in ".concat(name);
    if (entry.status === 'error') return "Invoice sync failed in ".concat(name);
    return "Pending invoice sync to ".concat(name);
};
export const toInvoiceRows = (param)=>{
    let { invoices, texts, adHocAgreementName } = param;
    return invoices.map((invoice)=>({
            id: invoice.id,
            invoiceNumber: invoice.formattedNumber,
            agreementName: invoice.isAdHoc ? adHocAgreementName : invoiceSummaryModel.relationshipTitle(invoice),
            status: getStatusText(invoice, texts),
            additionalInfo: getAdditionalInfoText(invoice, texts),
            issueDate: invoice.rawIssuedOn,
            dueDate: invoice.rawDueDate,
            total: invoice.rawTotalAmount
        }));
};
export const invoiceStatusSkins = (param)=>{
    let { invoices, texts } = param;
    return new Map(invoices.map((invoice)=>[
            getStatusText(invoice, texts),
            getStatusBadgeSkin(invoiceSummaryModel.displayStatus(invoice))
        ]));
};
export const invoiceMainColumn = (param)=>{
    let { texts, integrations, integrationTooltip } = param;
    return {
        type: 'text',
        name: 'invoiceNumber',
        header: texts.invoiceId,
        width: {
            initial: 130,
            min: 130,
            max: 180
        },
        sortable: true,
        bi: {
            name: 'invoice_id'
        },
        integrations,
        integrationIcon: 'sync',
        integrationTooltip
    };
};
export const invoiceColumns = (param)=>{
    let { texts, showAgreementName, statusSkin } = param;
    return [
        ...showAgreementName ? [
            {
                type: 'text',
                name: 'agreementName',
                header: texts.agreementName,
                width: {
                    initial: 150,
                    min: 150
                },
                sortable: true,
                bi: {
                    name: 'agreement_name'
                }
            }
        ] : [],
        {
            type: 'badge',
            name: 'status',
            header: texts.invoiceStatus,
            width: {
                initial: 96,
                min: 96
            },
            sortable: true,
            bi: {
                name: 'invoice_status'
            },
            useColor: statusSkin
        },
        {
            type: 'text',
            name: 'additionalInfo',
            header: texts.additionalInfo,
            width: {
                initial: 160,
                min: 160
            }
        },
        {
            type: 'date',
            name: 'issueDate',
            header: texts.issueDate,
            sortable: true,
            bi: {
                name: 'issue_date'
            }
        },
        {
            type: 'date',
            name: 'dueDate',
            header: texts.dueDate,
            sortable: true,
            bi: {
                name: 'due_date'
            }
        },
        {
            type: 'money',
            name: 'total',
            header: texts.total,
            width: {
                initial: 120,
                min: 110
            },
            hAlign: 'end',
            sortable: true,
            bi: {
                name: 'total'
            }
        }
    ];
};
export const invoiceSortColIndex = (param)=>{
    let { name, showAgreementName } = param;
    return invoiceColumnNames(showAgreementName).indexOf(name);
};
export const invoiceHref = (invoice)=>invoice == null ? '' : getURLWithFullPageInvoice({
        invoiceId: invoice.id,
        relationshipId: invoiceSummaryModel.relationshipId(invoice)
    });
export const invoiceColumnTexts = (texts)=>({
        invoiceId: texts.columns.invoiceId(),
        agreementName: texts.columns.agreementName(),
        invoiceStatus: texts.columns.invoiceStatus(),
        additionalInfo: texts.columns.additionalInfo(),
        issueDate: texts.columns.issueDate(),
        dueDate: texts.columns.dueDate(),
        total: texts.columns.total()
    });
export const clientInvoicesFilters = (param)=>{
    let { invoices, invoiceTexts, isVendor, showQboIntegration, showXeroIntegration } = param;
    const counters = getClientInvoicesFilterCounters([
        ...invoices
    ]);
    const qboStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: counters.qbo,
        texts: invoiceTexts.accountingStatusLabels
    });
    const xeroStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: counters.xero,
        texts: invoiceTexts.accountingStatusLabels
    });
    return {
        filterConfig: getTableFilterConfig({
            invoiceStatusFilterOptions: getInvoiceStatusFilterOptions({
                counters: counters.status,
                texts: invoiceTexts.invoiceDisplayStatusLabels
            }),
            issuesPreventingPaymentFilterOptions: getIssuesPreventingPaymentFilterOptions({
                counters: counters.paymentDetails,
                texts: invoiceTexts.paymentDetailLabels
            }),
            processedByFilterOptions: getProcessedByFilterOptions({
                counters: counters.paymentDetails,
                texts: invoiceTexts.processedByLabels
            }),
            qboStatusFilterOptions,
            xeroStatusFilterOptions
        }),
        activeFilterGroups: getActiveFilterGroups({
            showQboIntegration,
            showXeroIntegration,
            hasQboFilterOptions: qboStatusFilterOptions.length > 0,
            hasXeroFilterOptions: xeroStatusFilterOptions.length > 0,
            filterGroupLabels: invoiceTexts.filterGroupLabels,
            isVendor,
            hideDateChip: false
        })
    };
};
export const invoiceMobileFields = (showAgreementName, texts)=>({
        fields: compact([
            {
                name: 'status',
                inline: true
            },
            showAgreementName && {
                name: 'agreementName'
            },
            {
                name: 'additionalInfo'
            },
            {
                name: 'issueDate',
                label: texts.issueDate
            },
            {
                name: 'total',
                label: texts.total,
                hideColon: true,
                total: true
            }
        ])
    });
export const visibleInvoices = (param)=>{
    let { invoices, search, filters, now, sort, showAgreementName } = param;
    var _filters_InvoiceFilterType_InvoiceStatus, _filters_InvoiceFilterType_IssuesPreventingPayment, _filters_InvoiceFilterType_ProcessedBy, _filters_InvoiceFilterType_QboSync, _filters_InvoiceFilterType_XeroSync;
    const { since, until } = parseDateRangeFilter(filters[InvoiceFilterType.Date], now);
    const filtered = applyClientInvoicesFilters(invoices, _object_spread_props(_object_spread({}, EMPTY_FILTERS), {
        search,
        statuses: (_filters_InvoiceFilterType_InvoiceStatus = filters[InvoiceFilterType.InvoiceStatus]) !== null && _filters_InvoiceFilterType_InvoiceStatus !== void 0 ? _filters_InvoiceFilterType_InvoiceStatus : [],
        issuedOnSince: since,
        issuedOnUntil: until,
        paymentDetails: mergePaymentDetailsFilter({
            issuesPreventingPayment: (_filters_InvoiceFilterType_IssuesPreventingPayment = filters[InvoiceFilterType.IssuesPreventingPayment]) !== null && _filters_InvoiceFilterType_IssuesPreventingPayment !== void 0 ? _filters_InvoiceFilterType_IssuesPreventingPayment : [],
            processedBy: (_filters_InvoiceFilterType_ProcessedBy = filters[InvoiceFilterType.ProcessedBy]) !== null && _filters_InvoiceFilterType_ProcessedBy !== void 0 ? _filters_InvoiceFilterType_ProcessedBy : []
        }),
        qbo: (_filters_InvoiceFilterType_QboSync = filters[InvoiceFilterType.QboSync]) !== null && _filters_InvoiceFilterType_QboSync !== void 0 ? _filters_InvoiceFilterType_QboSync : AccountingStatusFilter.All,
        xero: (_filters_InvoiceFilterType_XeroSync = filters[InvoiceFilterType.XeroSync]) !== null && _filters_InvoiceFilterType_XeroSync !== void 0 ? _filters_InvoiceFilterType_XeroSync : AccountingStatusFilter.All
    }));
    return applyClientInvoicesSort(filtered, {
        colIndex: invoiceSortColIndex({
            name: sort.name,
            showAgreementName
        }),
        sort: sort.direction
    }, invoiceSortIds(showAgreementName));
};
